"use client";

import * as React from "react";
import { ChevronRightIcon } from "lucide-react";

import { cn } from "../../lib/utils";

/** Props for {@link FilterRow}. */
export type FilterRowProps = {
	/** Filter name shown on the left of the row. */
	label: React.ReactNode;
	/** Right-aligned summary of the current selection (e.g. "All", "Sequoia +2"). */
	summary?: React.ReactNode;
	/** When true, the row uses the primary tint to indicate an active selection. */
	isActive?: boolean;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type">;

/**
 * Tappable row used inside the mobile filter drawer as the trigger for a
 * nested option drawer. Renders label + summary + chevron with an active
 * (primary-tinted) state when a selection is present.
 */
const FilterRow = React.forwardRef<HTMLButtonElement, FilterRowProps>(
	({ label, summary, isActive = false, className, ...props }, ref) => (
		<button
			ref={ref}
			type="button"
			data-active={isActive ? "" : undefined}
			className={cn(
				"flex w-full items-center justify-between gap-3 border-t border-neutral-200 px-4 py-3 text-left text-sm first:border-t-0",
				"hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:outline-none",
				"disabled:cursor-not-allowed disabled:opacity-50",
				className,
			)}
			{...props}
		>
			<span
				className={cn(
					"shrink-0 font-medium",
					isActive ? "text-primary" : "text-neutral-900",
				)}
			>
				{label}
			</span>
			<span className="flex min-w-0 flex-1 items-center justify-end gap-2">
				{summary != null && (
					<span
						className={cn(
							"min-w-0 truncate",
							isActive ? "text-primary" : "text-neutral-500",
						)}
					>
						{summary}
					</span>
				)}
				<ChevronRightIcon
					aria-hidden="true"
					className={cn(
						"size-4 shrink-0",
						isActive ? "text-primary" : "text-neutral-400",
					)}
				/>
			</span>
		</button>
	),
);
FilterRow.displayName = "FilterRow";

export { FilterRow };
